'use client'

import { useContext, useState } from 'react'
import { Clock16 } from '@vinted/monochrome-icons'
import { Gift48 } from '@vinted/multichrome-icons'
import { Box as BoxExperimental } from '@vinted/playground'
import { Badge, Card, Cell, Container, Icon, Spacer, Text } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useBreakpoint } from '@marketplace-web/shared/breakpoints'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { UiStateContext } from '@marketplace-web/item-upload/item-upload-data'
import { VasEntryPoint, clickClaimYourGiftEvent } from '@marketplace-web/vas/vas-data'

import useFetchVasEntryPoints from '../../hooks/useFetchVasEntryPoints'
import BumpGiftModal from '../BumpGiftModal'

const BumpGiftBanner = () => {
  const { track } = useTracking()
  const breakpoints = useBreakpoint()
  const freeBumpFlowForNewMembersV2AbTest = useAbTest('free_bump_flow_for_new_members_v2')
  const { screen } = useContext(UiStateContext)

  const [isOpen, setIsOpen] = useState(false)

  const { data: vasEntryPoints } = useFetchVasEntryPoints(
    [VasEntryPoint.NewMemberBumpOnboardingListingForm],
    !freeBumpFlowForNewMembersV2AbTest,
  )

  const bumpGiftBanner = vasEntryPoints?.find(
    entryPoint => entryPoint.name === VasEntryPoint.NewMemberBumpOnboardingListingForm,
  )

  const handleModalOpen = () => {
    setIsOpen(true)

    track(
      clickClaimYourGiftEvent({
        screen,
      }),
    )
  }

  const handleModalClose = () => {
    setIsOpen(false)
  }

  if (!bumpGiftBanner) return null

  const { title, subtitle, badgeTitle, buttonTitle } = bumpGiftBanner

  return (
    <>
      <Card>
        <Container styling={breakpoints.tabletsUp ? Container.Styling.Wide : undefined}>
          <BoxExperimental
            theme="primary-extra-light"
            border="none"
            padding="tight"
            radius="medium"
            testId="bump-gift-banner"
            overflow="hidden"
          >
            <Cell
              title={title}
              prefix={<Icon name={Gift48} />}
              theme="transparent"
              type={Cell.Type.Navigating}
              onClick={handleModalOpen}
            >
              <Text
                as="span"
                text={subtitle}
                type={Text.Type.Subtitle}
                width={Text.Width.Parent}
                theme="amplified"
              />
              <Spacer size={Spacer.Size.Regular} />
              {buttonTitle && (
                <Text as="span" type={Text.Type.Subtitle} text={buttonTitle} clickable />
              )}
              {badgeTitle && (
                <Badge
                  icon={
                    <Icon
                      name={Clock16}
                      aria={{ 'aria-hidden': 'true' }}
                      color={Icon.Color.PrimaryDark}
                    />
                  }
                  content={badgeTitle}
                  theme="primary"
                  styling={Badge.Styling.Light}
                />
              )}
            </Cell>
          </BoxExperimental>
        </Container>
      </Card>
      <Spacer size={Spacer.Size.Regular} />
      <BumpGiftModal showDialog={isOpen} onClose={handleModalClose} />
    </>
  )
}

export default BumpGiftBanner
